import { DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Form, Input, List, message, Modal, Popconfirm, Space, Spin, Table, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import api from '~/services/api';
import { Container, IssueActions, IssueCard, ListContainer, TopSection } from './styles';

const { Text } = Typography;
const { TextArea } = Input;

// Zod validation schema
const issueSchema = z.object({
  description: z
    .string()
    .min(1, 'Description is required')
    .min(10, 'Description must be at least 10 characters long')
    .max(1000, 'Description must be less than 1000 characters'),
});

// AssignItemsModal component
const AssignItemsModal = ({ issueId, visible, onCancel, onSuccess }) => {
  const [searchText, setSearchText] = useState('');
  const [items, setItems] = useState([]);
  const [assignedItems, setAssignedItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  // Load existing assigned items when modal opens
  useEffect(() => {
    if (visible && issueId) {
      fetchAssignedItems(issueId);
    }
  }, [visible, issueId]);

  // Reset selections when modal closes
  useEffect(() => {
    if (!visible) {
      setSelectedRowKeys([]);
      setLastSelectedIndex(null);
      setSearchText('');
      setItems([]);
    }
  }, [visible]);

  const fetchAssignedItems = async (id) => {
    try {
      const { data } = await api.get(`inspection/issue/${id}`);
      setAssignedItems(data.issueItems || []);
    } catch (err) {
      console.error(err);
      message.error('Failed to load assigned items');
    }
  };

  const handleSearch = async () => {
    if (!searchText.trim()) return;
    setSearching(true);
    setSelectedRowKeys([]); // Clear selections when searching
    setLastSelectedIndex(null);
    try {
      const { data } = await api.get(`/items?is_active=true&search=${searchText}`);
      setItems(data);
    } catch (err) {
      console.error(err);
      message.error('Failed to search items');
    } finally {
      setSearching(false);
    }
  };

  const handleAddSelectedItems = () => {
    const itemsToAdd = items.filter((item) => selectedRowKeys.includes(item.id) && !assignedItems.find((assigned) => assigned.id === item.id));

    if (itemsToAdd.length > 0) {
      setAssignedItems((prev) => [...prev, ...itemsToAdd]);
      setSelectedRowKeys([]); // Clear selections after adding
      setLastSelectedIndex(null);
      message.success(`Added ${itemsToAdd.length} item(s)`);
    }
  };

  const handleRemoveItem = (id) => {
    setAssignedItems((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ids = assignedItems.map((i) => i.id);
      // console.log({
      //   issue_id: issueId,
      //   item_ids: ids,
      // });
      await api.post(`inspection/issue-items/${issueId}`, {
        item_ids: ids,
      });
      message.success('Assigned items updated!');
      onSuccess();
      onCancel();
    } catch (err) {
      console.error(err);
      message.error('Failed to update assigned items');
    } finally {
      setSaving(false);
    }
  };

  const handleRowClick = (record, index, event) => {
    const recordId = record.id;
    const isShiftPressed = event.shiftKey;

    if (isShiftPressed && lastSelectedIndex !== null) {
      // Handle range selection with Shift+click
      const startIndex = Math.min(lastSelectedIndex, index);
      const endIndex = Math.max(lastSelectedIndex, index);
      const rangeIds = items.slice(startIndex, endIndex + 1).map((item) => item.id);

      // Add range to selection (union with existing selection)
      const newSelectedKeys = Array.from(new Set([...selectedRowKeys, ...rangeIds]));
      setSelectedRowKeys(newSelectedKeys);
    } else {
      // Handle single selection (toggle)
      const newSelectedKeys = selectedRowKeys.includes(recordId) ? selectedRowKeys.filter((key) => key !== recordId) : [...selectedRowKeys, recordId];

      setSelectedRowKeys(newSelectedKeys);
      setLastSelectedIndex(index);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);
    },
    onSelect: (record, selected, selectedRows, nativeEvent) => {
      // Handle individual checkbox clicks
      const index = items.findIndex((item) => item.id === record.id);
      setLastSelectedIndex(index);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      // Handle select all checkbox
      setLastSelectedIndex(null);
    },
    getCheckboxProps: (record) => ({
      disabled: assignedItems.some((assigned) => assigned.id === record.id),
      name: record.name,
    }),
  };

  const searchColumns = [
    { title: 'Part Number', dataIndex: 'part_number', key: 'pn' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
  ];

  const selectedCount = selectedRowKeys.length;
  const availableToAdd = selectedRowKeys.filter((key) => !assignedItems.find((assigned) => assigned.id === key)).length;

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title="Manage Assigned Items"
      width="90%"
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save Changes ({assignedItems.length})
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', gap: 24, height: '70vh' }}>
        {/* Search & results */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
            <Input placeholder="Search items..." value={searchText} onChange={(e) => setSearchText(e.target.value)} onPressEnter={handleSearch} allowClear />
            <Button icon={<SearchOutlined />} onClick={handleSearch} loading={searching}>
              Search
            </Button>
          </Space.Compact>

          {items.length > 0 && (
            <Space style={{ marginBottom: 12 }}>
              <Button type="primary" onClick={handleAddSelectedItems} disabled={selectedCount === 0 || availableToAdd === 0}>
                Add Selected ({availableToAdd})
              </Button>
              <Text type="secondary">{selectedCount} selected • Hold Shift to select range</Text>
            </Space>
          )}

          <div style={{ flex: 1, overflow: 'auto' }}>
            {searching ? (
              <Spin style={{ display: 'block', margin: '40px auto' }} />
            ) : items.length > 0 ? (
              <Table
                dataSource={items}
                columns={searchColumns}
                rowKey="id"
                pagination={false}
                size="small"
                rowSelection={rowSelection}
                onRow={(record, index) => ({
                  onClick: (event) => handleRowClick(record, index, event),
                  style: {
                    cursor: 'pointer',
                    ...(assignedItems.some((assigned) => assigned.id === record.id)
                      ? {
                          backgroundColor: '#f5f5f5',
                          opacity: 0.6,
                        }
                      : {}),
                  },
                })}
              />
            ) : (
              <Empty description="Search to see results" />
            )}
          </div>
        </div>

        {/* Assigned items */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Text strong>Assigned Items ({assignedItems.length})</Text>
          <div style={{ flex: 1, marginTop: 12, overflow: 'auto' }}>
            {assignedItems.length > 0 ? (
              <List
                dataSource={assignedItems}
                rowKey="id"
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button type="link" danger size="small" onClick={() => handleRemoveItem(item.id)}>
                        Remove
                      </Button>,
                    ]}
                  >
                    <Text>
                      {item.part_number} - {item.name}
                    </Text>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No items assigned yet" />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

const AssignCustomersModal = ({ issueId, visible, onCancel, onSuccess, initialAssignedCustomers = [], onAssignedCustomersSaved }) => {
  const [searchText, setSearchText] = useState('');
  const [customers, setCustomers] = useState([]);
  const [assignedCustomers, setAssignedCustomers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  useEffect(() => {
    if (visible && issueId) {
      setAssignedCustomers(initialAssignedCustomers);

      const loadAssignedCustomers = async () => {
        try {
          const { data } = await api.get(`inspection/issue/${issueId}`);
          const serverAssignedCustomers = data.issueCustomers || [];

          if (serverAssignedCustomers.length > 0 || initialAssignedCustomers.length === 0) {
            setAssignedCustomers(serverAssignedCustomers);
          }
        } catch (err) {
          console.error(err);
          message.error('Failed to load assigned customers');
        }
      };

      loadAssignedCustomers();
    }
  }, [visible, issueId, initialAssignedCustomers]);

  useEffect(() => {
    if (!visible) {
      setSelectedRowKeys([]);
      setLastSelectedIndex(null);
      setSearchText('');
      setCustomers([]);
    }
  }, [visible]);

  const handleSearch = async () => {
    if (!searchText.trim()) return;
    setSearching(true);
    setSelectedRowKeys([]);
    setLastSelectedIndex(null);
    try {
      const { data } = await api.get('customers?size=100&page=0', {
        headers: {
          filterText: searchText.trim(),
        },
      });

      setCustomers(data.rows || []);
    } catch (err) {
      console.error(err);
      message.error('Failed to search customers');
    } finally {
      setSearching(false);
    }
  };

  const handleAddSelectedCustomers = () => {
    const customersToAdd = customers.filter(
      (customer) => selectedRowKeys.includes(customer.id) && !assignedCustomers.find((assigned) => assigned.id === customer.id)
    );

    if (customersToAdd.length > 0) {
      setAssignedCustomers((prev) => [...prev, ...customersToAdd]);
      setSelectedRowKeys([]);
      setLastSelectedIndex(null);
      message.success(`Added ${customersToAdd.length} customer(s)`);
    }
  };

  const handleRemoveCustomer = (id) => {
    setAssignedCustomers((prev) => prev.filter((customer) => customer.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ids = assignedCustomers.map((customer) => customer.id);

      await api.post(`inspection/issue-customers/${issueId}`, {
        customer_ids: ids,
      });

      if (onAssignedCustomersSaved) {
        onAssignedCustomersSaved(issueId, assignedCustomers);
      }

      message.success('Assigned customers updated!');
      onSuccess();
      onCancel();
    } catch (err) {
      console.error(err);
      message.error('Failed to update assigned customers');
    } finally {
      setSaving(false);
    }
  };

  const handleRowClick = (record, index, event) => {
    const recordId = record.id;
    const isShiftPressed = event.shiftKey;

    if (isShiftPressed && lastSelectedIndex !== null) {
      const startIndex = Math.min(lastSelectedIndex, index);
      const endIndex = Math.max(lastSelectedIndex, index);
      const rangeIds = customers.slice(startIndex, endIndex + 1).map((customer) => customer.id);

      const newSelectedKeys = Array.from(new Set([...selectedRowKeys, ...rangeIds]));
      setSelectedRowKeys(newSelectedKeys);
    } else {
      const newSelectedKeys = selectedRowKeys.includes(recordId) ? selectedRowKeys.filter((key) => key !== recordId) : [...selectedRowKeys, recordId];

      setSelectedRowKeys(newSelectedKeys);
      setLastSelectedIndex(index);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    onSelect: (record) => {
      const index = customers.findIndex((customer) => customer.id === record.id);
      setLastSelectedIndex(index);
    },
    onSelectAll: () => {
      setLastSelectedIndex(null);
    },
    getCheckboxProps: (record) => ({
      disabled: assignedCustomers.some((assigned) => assigned.id === record.id),
      name: record.name,
    }),
  };

  const searchColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
  ];

  const selectedCount = selectedRowKeys.length;
  const availableToAdd = selectedRowKeys.filter((key) => !assignedCustomers.find((assigned) => assigned.id === key)).length;

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title="Manage Assigned Customers"
      width="90%"
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save Changes ({assignedCustomers.length})
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', gap: 24, height: '70vh' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
            <Input
              placeholder="Search customers..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
            <Button icon={<SearchOutlined />} onClick={handleSearch} loading={searching}>
              Search
            </Button>
          </Space.Compact>

          {customers.length > 0 && (
            <Space style={{ marginBottom: 12 }}>
              <Button type="primary" onClick={handleAddSelectedCustomers} disabled={selectedCount === 0 || availableToAdd === 0}>
                Add Selected ({availableToAdd})
              </Button>
              <Text type="secondary">{selectedCount} selected • Hold Shift to select range</Text>
            </Space>
          )}

          <div style={{ flex: 1, overflow: 'auto' }}>
            {searching ? (
              <Spin style={{ display: 'block', margin: '40px auto' }} />
            ) : customers.length > 0 ? (
              <Table
                dataSource={customers}
                columns={searchColumns}
                rowKey="id"
                pagination={false}
                size="small"
                rowSelection={rowSelection}
                onRow={(record, index) => ({
                  onClick: (event) => handleRowClick(record, index, event),
                  style: {
                    cursor: 'pointer',
                    ...(assignedCustomers.some((assigned) => assigned.id === record.id)
                      ? {
                          backgroundColor: '#f5f5f5',
                          opacity: 0.6,
                        }
                      : {}),
                  },
                })}
              />
            ) : (
              <Empty description="Search to see results" />
            )}
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Text strong>Assigned Customers ({assignedCustomers.length})</Text>
          <div style={{ flex: 1, marginTop: 12, overflow: 'auto' }}>
            {assignedCustomers.length > 0 ? (
              <List
                dataSource={assignedCustomers}
                rowKey="id"
                renderItem={(customer) => (
                  <List.Item
                    actions={[
                      <Button type="link" danger size="small" onClick={() => handleRemoveCustomer(customer.id)}>
                        Remove
                      </Button>,
                    ]}
                  >
                    <Text>{customer.name || `Customer #${customer.id}`}</Text>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No customers assigned yet" />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default function InspectionIssues() {
  // State management
  const [issues, setIssues] = useState([]);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [isAssignCustomersModalVisible, setIsAssignCustomersModalVisible] = useState(false);
  const [assignedCustomersByIssue, setAssignedCustomersByIssue] = useState({});
  const [editingIssue, setEditingIssue] = useState(null);
  const [currentIssue, setCurrentIssue] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form instances
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // API call to fetch issues
  const fetchIssues = async () => {
    try {
      const { data: issuesData } = await api.get('inspection/issue');

      // Transform the data to match our component structure
      const transformedIssues = issuesData.map((issue) => ({
        ...issue,
        id: issue.id,
        createdAt: new Date(issue.createdAt),
        assignedItems: [], // Will be used later for item assignment
      }));

      setIssues(transformedIssues);
    } catch (error) {
      console.error('Error fetching issues:', error);
      message.error('Failed to load issues. Please refresh the page.');
    }
  };

  // Load issues when component mounts
  useEffect(() => {
    fetchIssues();
  }, []);

  // Validation helper
  const validateIssue = (values) => {
    try {
      issueSchema.parse(values);
      return { success: true, errors: null };
    } catch (error) {
      const errors = {};
      error.errors.forEach((err) => {
        errors[err.path[0]] = err.message;
      });
      return { success: false, errors };
    }
  };

  // Create new issue
  const handleCreateIssue = async (values) => {
    const validation = validateIssue(values);

    if (!validation.success) {
      // Set form errors
      Object.keys(validation.errors).forEach((field) => {
        createForm.setFields([
          {
            name: field,
            errors: [validation.errors[field]],
          },
        ]);
      });
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('inspection/issue', { description: values.description.trim() });

      // Use the actual response data from the API
      const newIssue = {
        ...response.data.data,
        createdAt: new Date(response.data.data.createdAt),
        assignedItems: [], // Will be used later for item assignment
      };

      setIssues((prev) => [newIssue, ...prev]);

      // Close modal and reset form
      setIsCreateModalVisible(false);
      createForm.resetFields();

      message.success('Issue created successfully!');
    } catch (error) {
      console.error('Error creating issue:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to create issue. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Edit existing issue
  const handleEditIssue = async (values) => {
    const validation = validateIssue(values);

    if (!validation.success) {
      // Set form errors
      Object.keys(validation.errors).forEach((field) => {
        editForm.setFields([
          {
            name: field,
            errors: [validation.errors[field]],
          },
        ]);
      });
      return;
    }

    setLoading(true);

    try {
      const response = await api.put(`inspection/issue/${editingIssue.id}`, {
        description: values.description.trim(),
      });

      // Update the issue in the local state with the response data
      setIssues((prev) =>
        prev.map((issue) =>
          issue.id === editingIssue.id
            ? {
                ...response.data.data,
                createdAt: new Date(response.data.data.createdAt),
                assignedItems: issue.assignedItems, // Keep existing assigned items
              }
            : issue
        )
      );

      // Close modal and reset form
      setIsEditModalVisible(false);
      setEditingIssue(null);
      editForm.resetFields();

      message.success('Issue updated successfully!');
    } catch (error) {
      console.error('Error updating issue:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to update issue. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete issue
  const handleDeleteIssue = async (issueId) => {
    try {
      await api.delete(`inspection/issue/${issueId}`);

      setIssues((prev) => prev.filter((issue) => issue.id !== issueId));
      message.success('Issue deleted successfully!');
    } catch (error) {
      console.error('Error deleting issue:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to delete issue. Please try again.');
      }
    }
  };

  // Open edit modal
  const openEditModal = (issue) => {
    setEditingIssue(issue);
    editForm.setFieldsValue({ description: issue.description });
    setIsEditModalVisible(true);
  };

  // Handle assign items
  const handleAssignItems = (issue) => {
    setCurrentIssue(issue);
    setIsAssignModalVisible(true);
  };

  const handleAssignCustomers = (issue) => {
    setCurrentIssue(issue);
    setIsAssignCustomersModalVisible(true);
  };

  const handleAssignedCustomersSaved = (issueId, customers) => {
    setAssignedCustomersByIssue((prev) => ({
      ...prev,
      [issueId]: customers,
    }));
  };

  const handleAssignSuccess = () => {
    // Refresh issues or update the specific issue with assigned items
    fetchIssues();
  };

  // Cancel modals with proper cleanup
  const handleCreateCancel = () => {
    createForm.resetFields();
    setIsCreateModalVisible(false);
  };

  const handleEditCancel = () => {
    editForm.resetFields();
    setEditingIssue(null);
    setIsEditModalVisible(false);
  };

  return (
    <Container>
      <TopSection>
        <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)} data-testid="add-issue-button">
          Add New Issue
        </Button>
      </TopSection>

      <ListContainer>
        {issues.length === 0 ? (
          <Empty description="No known issues created yet" image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)}>
              Create First Issue
            </Button>
          </Empty>
        ) : (
          <List
            dataSource={issues}
            renderItem={(issue) => (
              <List.Item key={issue.id}>
                <IssueCard>
                  <div className="issue-content">
                    <Text strong className="issue-description">
                      {issue.description}
                    </Text>
                    <Text type="secondary" className="issue-date">
                      Created: {issue.createdAt.toLocaleDateString()}
                    </Text>
                  </div>
                  <IssueActions>
                    <Space>
                      <Button type="default" icon={<LinkOutlined />} onClick={() => handleAssignItems(issue)} title="Assign Items">
                        Assign Items
                      </Button>
                      <Button type="default" icon={<LinkOutlined />} onClick={() => handleAssignCustomers(issue)} title="Assign Customers">
                        Assign Customers
                      </Button>
                      <Button type="default" icon={<EditOutlined />} onClick={() => openEditModal(issue)} title="Edit Issue" />
                      <Popconfirm
                        title="Delete Issue"
                        description="Are you sure you want to delete this issue?"
                        onConfirm={() => handleDeleteIssue(issue.id)}
                        okText="Yes"
                        cancelText="No"
                        placement="topRight"
                      >
                        <Button type="default" danger icon={<DeleteOutlined />} title="Delete Issue" />
                      </Popconfirm>
                    </Space>
                  </IssueActions>
                </IssueCard>
              </List.Item>
            )}
          />
        )}
      </ListContainer>

      {/* Create Issue Modal */}
      <Modal
        title="Create New Known Issue"
        open={isCreateModalVisible}
        onCancel={handleCreateCancel}
        footer={null}
        width={600}
        destroyOnClose
        focusTriggerAfterClose={false}
        maskClosable={false}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateIssue} autoComplete="off" preserve={false}>
          <Form.Item label="Issue Description" name="description" rules={[{ required: true, message: 'Please enter the issue description' }]}>
            <TextArea rows={4} placeholder="Describe the known issue in detail..." showCount maxLength={1000} autoFocus />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ float: 'right' }}>
              <Button onClick={handleCreateCancel} disabled={loading}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Issue
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Issue Modal */}
      <Modal
        title="Edit Known Issue"
        open={isEditModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        width={600}
        destroyOnClose
        focusTriggerAfterClose={false}
        maskClosable={false}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditIssue} autoComplete="off" preserve={false}>
          <Form.Item label="Issue Description" name="description" rules={[{ required: true, message: 'Please enter the issue description' }]}>
            <TextArea rows={4} placeholder="Describe the known issue in detail..." showCount maxLength={1000} autoFocus />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ float: 'right' }}>
              <Button onClick={handleEditCancel} disabled={loading}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Issue
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Items Modal */}
      <AssignItemsModal
        issueId={currentIssue?.id}
        visible={isAssignModalVisible}
        onCancel={() => setIsAssignModalVisible(false)}
        onSuccess={handleAssignSuccess}
      />

      <AssignCustomersModal
        issueId={currentIssue?.id}
        visible={isAssignCustomersModalVisible}
        onCancel={() => setIsAssignCustomersModalVisible(false)}
        onSuccess={handleAssignSuccess}
        initialAssignedCustomers={assignedCustomersByIssue[currentIssue?.id] || []}
        onAssignedCustomersSaved={handleAssignedCustomersSaved}
      />
    </Container>
  );
}
